import { AlertTriangle } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

/**
 * DIA-2 §2.3 — Persistent Lux Orange banner shown across the app while
 * an admin is previewing another role. Renders at the very top of the
 * viewport, above every layout header, so it's impossible to miss.
 *
 * Clicking "Exit preview" clears impersonation and triggers a user
 * re-fetch (handled by AuthContext.setImpersonateRole → useEffect →
 * fetchUser).
 */

const ROLE_LABELS = {
  surveyor:       'Surveyor',
  operative:      'Operative',
  sub_contractor: 'Sub-contractor',
};

const LUX_ORANGE = '#F5A623';
const LUX_ORANGE_INK = '#1E2530';

export function RolePreviewBanner({ scope = 'global' }) {
  const { isImpersonating, impersonateRole, setImpersonateRole } = useAuth();
  const location = useLocation();

  if (!isImpersonating) return null;
  // The global (app-root) instance yields to the portal's own in-header banner
  // so portal screens show a single, correctly-pinned bar (never a duplicate).
  if (scope === 'global' && location.pathname.startsWith('/portal')) return null;
  const label = ROLE_LABELS[impersonateRole] || impersonateRole;

  return (
    <div
      className="sticky top-0 z-[60] w-full flex items-center justify-center gap-3 px-4 py-2"
      style={{
        background: LUX_ORANGE,
        color: LUX_ORANGE_INK,
        boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
        paddingTop: scope === 'global' ? 'calc(0.5rem + env(safe-area-inset-top, 0px))' : undefined,
      }}
      data-testid="role-preview-banner"
      role="status"
      aria-live="polite"
    >
      <AlertTriangle className="w-4 h-4 flex-shrink-0" strokeWidth={2.5} />
      <span className="text-[13px] font-semibold">
        Previewing as <span data-testid="role-preview-banner-role">{label}</span>
      </span>
      <span className="text-[12px] hidden sm:inline opacity-80">
        · role-based permissions apply live
      </span>
      <button
        type="button"
        onClick={() => setImpersonateRole(null)}
        className="ml-2 text-[12px] font-bold px-2.5 py-1 rounded-md transition-colors hover:opacity-90"
        style={{ background: LUX_ORANGE_INK, color: LUX_ORANGE }}
        data-testid="role-preview-exit-btn"
      >
        Exit preview
      </button>
    </div>
  );
}

export default RolePreviewBanner;
